import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map, mergeMap, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { MOCK_ROUTES } from 'src/app/components/reports/reports.constants';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SendAgain } from 'src/app/core/models/reports/reports.model';
import { OnRoadOperationsService } from 'src/app/core/services/reports/on-road-operations/on-road-operations.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import { Options } from '../../../shared/report-format/report-format.constants';
import {
  CONSTS,
  PDF_REPORT,
  REPORT_FILE_NAME,
  SendAgainFormGroup,
} from './send-again.constants';

@Component({
  selector: 'app-send-again',
  templateUrl: './send-again.component.html',
  styleUrls: ['./send-again.component.scss'],
  standalone: false,
})
export class SendAgainComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  readonly data: SendAgainFormGroup = inject(MAT_DIALOG_DATA);
  private userService = inject(UserService);
  private dialogRef: MatDialogRef<SendAgainComponent> = inject(
    MatDialogRef<SendAgainComponent>,
  );
  private fb: FormBuilder = inject(FormBuilder);
  private onRoadOperationService: OnRoadOperationsService = inject(
    OnRoadOperationsService,
  );
  private reportUtils: ReportsUtils = inject(ReportsUtils);
  private commonUtils: CommonUtils = inject(CommonUtils);

  readonly CONSTS = CONSTS;
  readonly IMAGES = IMAGE_SRC;
  readonly PDF_REPORT = PDF_REPORT;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  sendAgainForm!: FormGroup;
  routes = MOCK_ROUTES;
  locationcode = '';
  sendAgainReport!: SendAgain[];
  driver!: string;

  @ViewChild('pdfContainer', { read: ViewContainerRef })
  pdfContainer!: ViewContainerRef;

  ngOnInit(): void {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        mergeMap((user) => {
          this.locationcode = user?.location;
          return this.getRouteList();
        }),
      )
      .subscribe();

    this.sendAgainForm = this.fb.group({
      route: [this.routes[0]],
      reportFormat: [Options.PDF],
    });
  }

  /**
   * Used to get route list
   *
   * @private
   * @memberof SendAgainComponent
   */
  private getRouteList() {
    return this.onRoadOperationService
      .getSendAgainRoutes(this.locationcode)
      .pipe(
        take(1),
        map((data) => {
          this.routes = data.response.sendAgainRoutes
            .map(({ route }) => {
              return {
                label: route,
                value: route,
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label));
          this.routes.unshift({
            label: $localize`All`,
            value: 'All',
          });
        }),
      );
  }

  onExit() {
    this.commonUtils.openExitModal(this.sendAgainForm.dirty);
  }

  radioChange(newValue: Options) {
    this.sendAgainForm.get('reportFormat')?.setValue(newValue);
  }

  /**
   * Used to get report in CSV or PDF file format
   *
   * @memberof SendAgainComponent
   */
  getReport() {
    const formValue = this.sendAgainForm.value;

    const request = {
      locationcode: this.locationcode,
      loop: formValue.route?.value === 'All' ? 'All' : formValue.route,
    };
    this.onRoadOperationService
      .getSendAgainReport(request)
      .pipe(take(1))
      .subscribe((data) => {
        const formatType = this.sendAgainForm.get('reportFormat')?.value;
        const sendAgain = data.response.sendAgain;
        if (sendAgain?.length) {
          if (formatType === Options.CSV) {
            const customHeader = [
              ['', '', '', `${'SEND AGAIN'}`, '', '', ''],
              ['', 'DRIVER', '', '', '', `${data.response.driver}`],
            ];
            this.reportUtils.exportCSVReport(
              sendAgain,
              REPORT_FILE_NAME,
              customHeader,
            );
          }
          if (formatType === Options.PDF) {
            this.sendAgainReport = sendAgain;
            this.driver = data.response.driver;
            setTimeout(() => {
              const content = this.pdfContainer.element.nativeElement.innerHTML;
              this.reportUtils.exportPdfReport(
                content,
                PDF_REPORT.NAME,
                REPORT_FILE_NAME,
              );
            }, 0);
          }
        } else {
          this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
        }
        this.dialogRef.close();
      });
  }
}
